/**
 * AgentPricingPlans — preçário Observador / Premium (design do documento).
 * Usado na página pública /precos-agentes e na página do agente logado.
 */
import { useState } from 'react';
import { Check, Minus, Loader2 } from 'lucide-react';
import './agent/agent_brand.css';

// Zonamento: these pages sit outside the night Cockpit, so the switch turns them to the day palette.
const INK = 'var(--brand-on-primary, #1A1B2E)', INK2 = 'var(--brand-surface, #22243B)', LINE = 'var(--brand-line, #33355A)';
const PURPLE = 'var(--brand-primary, #A78BD9)', PAPER = 'var(--brand-ink, #FAFAF7)', DIM = 'var(--brand-muted, #B9BACB)';
const TEAL = 'var(--brand-muted, #3CC8B5)'; // eyebrows and the "2 meses grátis" tag
const CHECK = 'var(--brand-good-text, #3CC8B5)'; // included-feature ticks
const PREMIUM_CELL = 'var(--brand-ink, #3CC8B5)';
const OFF = (alpha) => `var(--brand-muted, rgba(255,255,255,${alpha}))`;
const LINE_STRONG = 'var(--brand-line-strong, #33355A)';

const MONO = { fontFamily: "var(--brand-font-mono, 'IBM Plex Mono', ui-monospace, monospace)" };
// Prices: Roboto Mono with the switch on, the page face (unchanged) with it off.
const PRICE_MONO = { fontFamily: 'var(--brand-font-mono, inherit)' };

const PRICES = {
  monthly: { amount: '29,99 €', per: '/ mês', billed: 'Cancelas quando quiseres.', cta: 'Ativar Premium', fine: 'FATURAÇÃO MENSAL · CANCELA A QUALQUER MOMENTO' },
  annual: { amount: '24,99 €', per: '/ mês', billed: 'Faturado 299,90 € por ano — poupas 59,98 €.', cta: 'Ativar Premium anual', fine: 'FATURAÇÃO ANUAL · EQUIVALE A 2 MESES GRÁTIS' },
};

const OBSERVADOR = [
  { ok: true, t: 'Primeiro nome, fotografia e resumo do Buyer DNA dos compradores da tua zona' },
  { ok: true, t: 'Índice da Procura: preços e zonas mais pedidos' },
  { ok: true, t: 'Filtro por concelho, tipologia e escalão de preço' },
  { ok: true, t: 'Vês quais dos compradores têm miD' },
  { ok: false, t: 'Submeter imóveis a compradores' },
  { ok: false, t: 'Alertas de novos compradores' },
  { ok: false, t: 'Selo de agente miD' },
];

const PREMIUM = [
  'Tudo o que o Observador tem, mais:',
  'Submeter imóveis aos compradores da tua zona',
  'Até 30 submissões por mês, com limite por comprador para evitar ruído',
  'Contacto com o comprador apenas no miD, depois de ele aceitar o match',
  'Alertas em tempo real de novos compradores nas tuas zonas',
  'Selo «Trabalho com compradores miD» para o teu perfil e materiais',
  'Histórico de submissões e taxa de aceitação',
];

const TABLE = [
  ['Buyer DNA de compradores', 'Todos', 'Todos'],
  ['Primeiro nome e fotografia do comprador', 'Sim', 'Sim'],
  ['Resumo do Buyer DNA do comprador', 'Sim', 'Sim'],
  ['Estado «tem miD» visível', 'Sim', 'Sim'],
  ['Índice da Procura da zona', 'Sim', 'Sim'],
  ['Submeter imóvel a um comprador', 'Não', 'Até 30 / mês'],
  ['Contacto com o comprador', 'Não', 'No miD, após match aceite'],
  ['Alertas de novos compradores', 'Não', 'Tempo real'],
  ['Selo de agente miD', 'Não', 'Sim'],
  ['Métricas de desempenho', 'Não', 'Sim'],
];

const FAQ = [
  ['Porque é que o plano gratuito mostra tudo?', 'Porque não queremos que pagues para descobrir se há procura na tua zona. Vês o resumo do Buyer DNA e decides com informação. O que o Premium desbloqueia é a ação: enviares os teus imóveis a esses compradores.'],
  ['Vejo quem são os compradores?', 'Sim. Em ambos os planos vês o primeiro nome e a fotografia e um resumo do Buyer DNA: agregado, tipologia, orçamento, zonas, fase de preparação e arquétipo. O email e o telefone nunca são partilhados. O contacto acontece no miD, depois de o comprador aceitar o teu match ou pedir uma visita. Nessas visitas e nos matches aceites, vês o primeiro nome em ambos os planos.'],
  ['Porquê um limite de 30 submissões?', 'Para o miD não se transformar em mais um canal de envio em massa. Um comprador que recebe trinta imóveis irrelevantes deixa de abrir as notificações, e o teu match seguinte perde valor. O limite protege a atenção do lado que interessa aos dois.'],
  ['O que significa um comprador «ter miD»?', 'Resumo do Buyer DNA definido, solução de crédito encontrada com um intermediário e processo pronto para avançar. É o nosso patamar mais alto de qualificação — e vês no Cockpit quais dos compradores já lá chegaram.'],
  ['Tenho uma equipa. Há plano para agências?', 'Sim. A partir de cinco agentes o preço por lugar desce e a gestão passa a ser centralizada. Fala connosco para montarmos o plano.'],
  ['Posso cancelar?', 'A qualquer momento, no plano mensal, com efeito no fim do período já pago. No plano anual mantém-se o acesso até ao fim dos doze meses.'],
];

export const AgentPricingPlans = ({ onPremiumCta, onFreeCta, initialCycle, premiumBusy = false, freeCtaLabel = 'Criar conta gratuita' }) => {
  const [cycle, setCycle] = useState(initialCycle === 'annual' ? 'annual' : 'monthly');
  const p = PRICES[cycle];

  return (
    <div className="agent-pricing" style={{ color: PAPER }}>
      {/* Toggle */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div className="agent-control" style={{ display: 'inline-flex', gap: 6, background: INK2, border: `1px solid ${LINE_STRONG}`, borderRadius: 999, padding: 6 }} data-testid="pricing-toggle">
          {['monthly', 'annual'].map((k) => (
            <button key={k} type="button" onClick={() => setCycle(k)}
              data-testid={`pricing-toggle-${k}`}
              className="agent-control"
              style={{
                fontSize: 15, fontWeight: 500, cursor: 'pointer', border: 0,
                padding: '12px 26px', borderRadius: 999, transition: 'all .18s',
                background: cycle === k ? PURPLE : 'transparent',
                color: cycle === k ? INK : 'var(--brand-ink, #A9AAC2)', display: 'inline-flex', alignItems: 'center',
              }}>
              {k === 'monthly' ? 'Mensal' : 'Anual'}
              {k === 'annual' && (
                <span className="agent-square" style={{ ...MONO, fontSize: 10.5, letterSpacing: '0.14em', marginLeft: 10, padding: '5px 10px', borderRadius: 8, border: `1px solid ${cycle === k ? 'var(--brand-line, rgba(26,27,46,.35))' : 'var(--brand-line, rgba(60,200,181,.4))'}`, background: cycle === k ? 'var(--brand-surface-2, rgba(26,27,46,.16))' : 'var(--brand-surface-2, rgba(60,200,181,.12))', color: cycle === k ? 'var(--brand-ink, #1A1B2E)' : 'var(--brand-ink, #3CC8B5)' }}>
                  2 MESES GRÁTIS
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, padding: '34px 0 60px' }}>
        {/* Observador */}
        <div className="agent-square" style={{ background: INK2, border: `1.5px solid ${LINE}`, borderRadius: 28, padding: '38px 34px', display: 'flex', flexDirection: 'column' }} data-testid="plan-observador">
          <div className="agent-heading" style={{ fontSize: 22, fontWeight: 600 }}>Observador</div>
          <div style={{ fontSize: 13.5, color: DIM, marginTop: 4 }}>Grátis, para sempre</div>
          <div style={{ marginTop: 22 }}>
            <span style={{ fontSize: 44, fontWeight: 600, letterSpacing: '-0.03em', ...PRICE_MONO }}>0 €</span>
          </div>
          <div style={{ ...MONO, fontSize: 10.5, letterSpacing: '0.18em', color: DIM, marginTop: 6 }}>SEM CARTÃO DE CRÉDITO</div>
          <p style={{ fontSize: 14, color: DIM, marginTop: 16 }}>Acesso completo à leitura da procura.</p>
          <ul style={{ listStyle: 'none', margin: '22px 0 28px', padding: 0, display: 'grid', gap: 11, flex: 1 }}>
            {OBSERVADOR.map((f) => (
              <li key={f.t} style={{ display: 'flex', gap: 10, fontSize: 13.5, color: f.ok ? PAPER : OFF(0.32), alignItems: 'flex-start' }}>
                {f.ok ? <Check size={15} style={{ color: CHECK, marginTop: 2, flexShrink: 0 }} /> : <Minus size={15} style={{ color: OFF(0.25), marginTop: 2, flexShrink: 0 }} />}
                {f.t}
              </li>
            ))}
          </ul>
          <button type="button" onClick={onFreeCta} data-testid="plan-free-cta" className="agent-control"
            style={{ width: '100%', padding: '15px 0', borderRadius: 999, cursor: 'pointer', fontSize: 15, fontWeight: 600, background: 'transparent', color: PAPER, border: `1.5px solid ${LINE_STRONG}` }}>
            {freeCtaLabel}
          </button>
          <div style={{ ...MONO, fontSize: 10, letterSpacing: '0.18em', color: OFF(0.35), textAlign: 'center', marginTop: 12 }}>SEM COMPROMISSO</div>
        </div>

        {/* Premium */}
        <div className="agent-square agent-flat" style={{ position: 'relative', background: 'var(--brand-surface, linear-gradient(165deg,#2A2C4A,#1E1F35))', border: '1.5px solid var(--brand-primary, #584a8f)', borderRadius: 28, padding: '38px 34px', display: 'flex', flexDirection: 'column', boxShadow: 'var(--brand-shadow-float, 0 30px 70px rgba(0,0,0,.4))' }} data-testid="plan-premium">
          <span className="agent-square" style={{ ...MONO, position: 'absolute', top: -13, left: 34, fontSize: 10.5, letterSpacing: '0.2em', background: PURPLE, color: INK, borderRadius: 8, padding: '6px 12px', fontWeight: 600 }}>RECOMENDADO</span>
          <div className="agent-heading" style={{ fontSize: 22, fontWeight: 600 }}>Premium</div>
          <div style={{ fontSize: 13.5, color: DIM, marginTop: 4 }}>Para quem quer fechar negócio</div>
          <div style={{ marginTop: 22 }}>
            <span style={{ fontSize: 44, fontWeight: 600, letterSpacing: '-0.03em', ...PRICE_MONO }} data-testid="premium-price">{p.amount}</span>
            <span style={{ fontSize: 16, color: DIM, marginLeft: 6 }}>{p.per}</span>
          </div>
          <div style={{ ...MONO, fontSize: 10.5, letterSpacing: '0.18em', color: DIM, marginTop: 6 }}>POR AGENTE · ACRESCE IVA À TAXA LEGAL</div>
          <p style={{ fontSize: 14, color: DIM, marginTop: 16 }} data-testid="premium-billed">{p.billed}</p>
          <ul style={{ listStyle: 'none', margin: '22px 0 28px', padding: 0, display: 'grid', gap: 11, flex: 1 }}>
            {PREMIUM.map((t, i) => (
              <li key={t} style={{ display: 'flex', gap: 10, fontSize: 13.5, alignItems: 'flex-start', fontWeight: i === 0 ? 600 : 400 }}>
                <Check size={15} style={{ color: CHECK, marginTop: 2, flexShrink: 0 }} /> {t}
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => onPremiumCta(cycle)} disabled={premiumBusy} data-testid="plan-premium-cta" className="agent-control"
            style={{ width: '100%', padding: '15px 0', borderRadius: 999, cursor: 'pointer', fontSize: 15, fontWeight: 700, background: PURPLE, color: INK, border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: premiumBusy ? 0.7 : 1 }}>
            {premiumBusy && <Loader2 size={16} className="animate-spin" />}
            {p.cta}
          </button>
          <div style={{ ...MONO, fontSize: 10, letterSpacing: '0.18em', color: OFF(0.35), textAlign: 'center', marginTop: 12 }}>{p.fine}</div>
        </div>
      </div>

      {/* Comparação */}
      <div style={{ marginBottom: 60 }}>
        <div style={{ ...MONO, fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: TEAL }}>Comparação</div>
        <h2 className="agent-heading" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.03em', margin: '10px 0 22px' }}>Onde está a fronteira</h2>
        <div className="agent-square" style={{ overflowX: 'auto', borderRadius: 20, border: `1px solid ${LINE}` }} data-testid="pricing-comparison-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr style={{ background: INK2 }}>
                {['Funcionalidade', 'Observador', 'Premium'].map((h) => (
                  <th key={h} style={{ ...MONO, textAlign: 'left', fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: DIM, padding: '14px 18px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TABLE.map(([f, o, pr]) => (
                <tr key={f} style={{ borderTop: `1px solid ${LINE}` }}>
                  <td style={{ padding: '13px 18px', fontSize: 13.5 }}>{f}</td>
                  <td style={{ padding: '13px 18px', fontSize: 13.5, color: o === 'Não' || o === 'Nunca' ? OFF(0.35) : DIM }}>{o}</td>
                  <td style={{ padding: '13px 18px', fontSize: 13.5, color: PREMIUM_CELL, fontWeight: 600 }}>{pr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ ...MONO, fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: TEAL }}>Perguntas</div>
        <h2 className="agent-heading" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.03em', margin: '10px 0 22px' }}>Antes de decidires</h2>
        <div style={{ display: 'grid', gap: 10 }} data-testid="pricing-faq">
          {FAQ.map(([q, a]) => (
            <details key={q} className="agent-square" style={{ background: INK2, border: `1px solid ${LINE}`, borderRadius: 16, padding: '16px 20px' }}>
              <summary style={{ fontSize: 14.5, fontWeight: 600, cursor: 'pointer', listStyle: 'none' }}>{q}</summary>
              <p style={{ fontSize: 13.5, color: DIM, lineHeight: 1.6, marginTop: 10 }}>{a}</p>
            </details>
          ))}
        </div>
      </div>

      <div style={{ ...MONO, fontSize: 10, letterSpacing: '0.22em', color: OFF(0.3), textAlign: 'center', paddingBottom: 20 }}>
        miD · MEDIADOR IMOBILIÁRIO DIGITAL — PREÇOS SEM IVA · POR AGENTE
      </div>
    </div>
  );
};

export default AgentPricingPlans;
